/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Stack, Typography } from "@mui/material";
import { HeatMixImportantValues } from "./HeatMixImportantValues";
import { HeatMixPlot } from "./HeatMixPlot";
import { useMaterialColor, UNKNOWN_MATERIAL_NAME } from "hooks/useMaterials";
import { useMemo } from "react";
import { HeatMixAncestors } from "./HeatMixAncestors";
import {
  HeatMix,
  MaterialQuantityUnit,
  MaterialRead,
} from "src/store/api/generatedApi";
import { useIsAdmin } from "hooks/settings";
import {
  MixDisplayMode,
  MixViewAs,
  mixValueModeFor,
  mixViewFor,
} from "../types";
import { useTranslation } from "react-i18next";

interface HeatMixDetailItemProps {
  stageMix: HeatMix;
  referenceMix?: HeatMix;
  isFirst: boolean;
  displayMode: MixDisplayMode;
  ignoredMaterials: Set<string>;
  materialMetadata: MaterialRead[];
  highlightedMaterial?: string;
}

const toPercentages = (
  values: Record<string, number>
): Record<string, number> => {
  const total = Object.values(values).reduce((sum, v) => sum + v, 0);
  if (total <= 0)
    return Object.fromEntries(Object.keys(values).map((k) => [k, 0]));
  return Object.fromEntries(
    Object.entries(values).map(([k, v]) => [k, (100 * v) / total])
  );
};

const getTotalsByMaterial = (mixView: MixViewAs, stageMix: HeatMix) => {
  switch (mixView) {
    case "mass":
      return Object.fromEntries(
        stageMix.mix.totals.map(({ material_name, mass }) => [
          material_name,
          mass,
        ])
      );
    case "lifts":
      return Object.fromEntries(
        stageMix.mix.lifts.map(({ material_name, lifts }) => [
          material_name,
          lifts,
        ])
      );
    case "liftRecipe":
      return Object.fromEntries(
        stageMix.mix.lifts.map(({ material_name, lifts, normalised_lifts }) => [
          material_name,
          normalised_lifts ?? lifts,
        ])
      );
  }
};

const getDiffsByMaterial = (mixView: MixViewAs, stageMix: HeatMix) => {
  switch (mixView) {
    case "mass":
      return Object.fromEntries(
        stageMix.mix.diffs.map(({ material_name, mass }) => [
          material_name,
          mass,
        ])
      );
    case "lifts":
      return Object.fromEntries(
        stageMix.mix.lift_diffs.map(({ material_name, lifts }) => [
          material_name,
          lifts,
        ])
      );
    case "liftRecipe":
      return Object.fromEntries(
        stageMix.mix.lift_diffs.map(
          ({ material_name, lifts, normalised_lifts }) => [
            material_name,
            normalised_lifts ?? lifts,
          ]
        )
      );
  }
};

const computePercentagePointDiffs = (
  currentRawTotals: Record<string, number>,
  referenceMix: HeatMix | undefined,
  mixView: MixViewAs
): Record<string, number> => {
  if (!referenceMix) {
    return Object.fromEntries(Object.keys(currentRawTotals).map((k) => [k, 0]));
  }
  const currentPct = toPercentages(currentRawTotals);
  const referencePct = toPercentages(
    getTotalsByMaterial(mixView, referenceMix)
  );
  return Object.fromEntries(
    Object.keys(currentPct).map((name) => [
      name,
      currentPct[name]! - (referencePct[name] ?? 0),
    ])
  );
};

const getUnitsByMaterial = (
  mixView: MixViewAs,
  stageMix: HeatMix
): Record<string, MaterialQuantityUnit> => {
  switch (mixView) {
    case "mass":
      return Object.fromEntries(
        stageMix.mix.totals.map(({ material_name }) => [material_name, "mass"])
      );
    case "lifts":
      return Object.fromEntries(
        stageMix.mix.lifts.map(({ material_name }) => [material_name, "grab"])
      );
    case "liftRecipe":
      return Object.fromEntries(
        stageMix.mix.lifts.map(({ material_name, normalised_lifts }) => [
          material_name,
          normalised_lifts === null ? "grab" : "ratio",
        ])
      );
  }
};

export const HeatMixDetailItem = ({
  stageMix,
  referenceMix,
  isFirst,
  displayMode,
  ignoredMaterials,
  materialMetadata,
  highlightedMaterial,
}: HeatMixDetailItemProps) => {
  const isAdmin = useIsAdmin();
  const { t } = useTranslation();
  const { addColors } = useMaterialColor();

  const mixView = mixViewFor(displayMode);
  const mixValueMode = mixValueModeFor(displayMode);
  const isPercent = mixValueMode === "mass_fraction";

  const materials = useMemo(() => {
    const rawTotals = getTotalsByMaterial(mixView, stageMix);
    const totalsByMaterial = isPercent ? toPercentages(rawTotals) : rawTotals;
    const diffsByMaterial = isPercent
      ? computePercentagePointDiffs(rawTotals, referenceMix, mixView)
      : getDiffsByMaterial(mixView, stageMix);
    const unitsByMaterial = getUnitsByMaterial(mixView, stageMix);
    const materialsInMix = Object.keys(totalsByMaterial);
    const shownMaterialNames = materialMetadata
      .map((material) => material.name)
      .filter((name) => materialsInMix.includes(name));
    const materialNames = materialsInMix.includes(UNKNOWN_MATERIAL_NAME)
      ? [...shownMaterialNames, UNKNOWN_MATERIAL_NAME]
      : shownMaterialNames;

    const materialsWithColors = addColors(
      materialNames.map((name) => ({
        name,
        value: totalsByMaterial[name]!,
        unit: unitsByMaterial[name]!,
        diffFromPreviousStage: diffsByMaterial[name] ?? 0,
        ignored: [...ignoredMaterials].includes(name),
        highlighted: name === highlightedMaterial,
      }))
    );

    if (!highlightedMaterial) {
      return materialsWithColors;
    }

    const highlighted = materialsWithColors.find(
      (m) => m.name === highlightedMaterial
    );
    if (!highlighted) {
      return materialsWithColors;
    }

    return [
      ...materialsWithColors.filter((m) => m.name !== highlightedMaterial),
      highlighted,
    ];
  }, [
    addColors,
    stageMix,
    mixView,
    isPercent,
    highlightedMaterial,
    referenceMix,
    materialMetadata,
    ignoredMaterials,
  ]);

  return (
    <Stack
      direction="column"
      alignItems="center"
      sx={{ flex: 1, minWidth: 250 }}
      gap={1}
    >
      <Typography variant="h5" textAlign="center">
        {t(stageMix.source)}
      </Typography>

      <HeatMixImportantValues stageMix={stageMix} isFirst={isFirst} />

      <HeatMixPlot
        materials={materials}
        isFirst={isFirst}
        mixValueMode={mixValueMode}
      />
      {stageMix.plan_id === null ||
      stageMix.mix_id === null ||
      !isAdmin ? null : (
        <HeatMixAncestors planId={stageMix.plan_id} mixId={stageMix.mix_id} />
      )}
    </Stack>
  );
};
